





















































































































import { BusinessUnit } from "@/shared/models/businessUnit";
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import {
  MessageViewModel,
  MessageRecipientViewModel,
  MessageAttachmentViewModel,
} from "./types";
import RightDrawer from "@/components/misc/RightDrawer.vue";
import ScrollLoader from "@/components/misc/ScrollLoader.vue";
import { saveFile } from "@/shared/saveFile";
import * as _ from "lodash";
import { Getter } from "vuex-class";
import { formatDateTimeAccordingToTimeZoneOrUTC } from '@/shared/dateFormatUtils';

@Component({
  components: {
    RightDrawer,
    ScrollLoader,
  },
})
export default class ConversationPanel extends Vue {
  @Prop({ default: false }) open!: boolean;
  @Prop() messageId?: number;

  model: MessageViewModel | null = null;
  loading = false;

  @Getter("businessUnit/activeBusinessUnit") activeBusinessUnit?: BusinessUnit;
  @Getter("language/language") language?: string;

  @Watch("open")
  async openWatch(fresh: boolean, old: boolean) {
    if (fresh && !old) {
      await this.load();
    } else {
      this.model = null;
      this.loading = false;
      this.done = false;
      this.initialLoadDone = false;
      this.downloadingAttachment = false;
    }
  }

  async load(){
    this.loading = true;
    this.done = false;
    await this.axios
      .get<MessageViewModel>("/messages/" + this.messageId)
      .then((res) => {
        this.model = res.data;
        this.initialLoadDone = true;
      })
      .finally(() => {
        this.loading = false;
      });
      if (this.scrollEl) {
        if (
          this.scrollEl.scrollHeight > 0 &&
          this.scrollEl.scrollHeight <= this.scrollEl.clientHeight &&
          !this.done
        ) {
          await this.loadMore();
        }
      }
  }

  close() {
    this.$emit("update:open", false);
  }

  get subject() {
    if (this.model?.subject) {
      return this.model.subject;
    }
    let bodyReplacement =
      this.model?.body.replaceAll(RegExp(/<[a-zA-Z/].*?>/gm), " ") ?? "";
    if (bodyReplacement.length > 100) {
      bodyReplacement = bodyReplacement.substring(0, 100) + "...";
    }
    return bodyReplacement;
  }

  getOldestParent(message: MessageViewModel): MessageViewModel {
    if (message?.parentMessage) {
      return this.getOldestParent(message.parentMessage);
    } else {
      return message;
    }
  }

  get isRead(){
    return this.model?.isRead ?? false;
  }

  get isResolved(){
    return this.model?.isResolved ?? false;
  }

  getMessageList(message: MessageViewModel): MessageViewModel[] {
    if (!message) {
      return [];
    }

    if (message?.parentMessage) {
      return [...this.getMessageList(message.parentMessage), message];
    } else {
      return [message];
    }
  }

  get messages() {
    if (!this.model) return [];
    return this.getMessageList(this.model).reverse();
  }

  getRecipients(
    message: MessageViewModel,
    recipientType: "main" | "cc" 
  ) {
    let recipients = [] as MessageRecipientViewModel[];
    switch (recipientType) {
      case "main":
        recipients = message.messageRecipients.filter(
          (x) => !x.isCc && !x.isBcc
        );
        break;
      case "cc":
        recipients = message.messageRecipients.filter((x) => x.isCc);
        break;
      default:
        return [];
    }

    return recipients.map((x) => x.to).join(", ");
  }

  getAttachmentIcon(mimeType: string) {
    if (mimeType.includes("image")) {
      return "fal fa-image";
    } else if (mimeType.includes("pdf")) {
      return "fal fa-file-pdf";
    } else if (mimeType.includes("html")) {
      return "fal fa-file-code";
    } else if (
      mimeType.includes("openxmlformats-officedocument.spreadsheetml")
    ) {
      return "fal fa-file-excel";
    } else {
      return "fal fa-file";
    }
  }

  downloadingAttachment = false;
  async downloadAttachment(
    messageId: number,
    attachment: MessageAttachmentViewModel
  ) {
    this.downloadingAttachment = true;
    await this.$http
      .get(`messages/${messageId}/${attachment.id}`, {
        responseType: "blob",
      })
      .then((res) => {
        const groups = /filename=(?<filename>[^;]+);/.exec( 
          res.headers["content-disposition"]
        )?.groups;
        const name = groups?.filename.replace(/(^"|"$)/g, '') ?? attachment.name
        saveFile(res.data, name);
      })
      .finally(() => {
        this.downloadingAttachment = false;
      });
  }

  scrollEl = null as HTMLElement | null;
  mounted() {
    this.scrollEl = this.$refs.messages as HTMLElement;
  }

  markAsRead() {
    this.$emit('markAsRead', this.messageId);
  }
  markAsResolved() {
    this.$emit('markAsResolved', this.messageId)
  }
  reply() {
    this.$emit('reply', this.model);
  }
  replyToAll() {
    this.$emit('replyToAll', this.model)
  }
  forward() {
    this.$emit('forward', this.model)
  }

  initialLoadDone = false;
  done = false;
  async loadMore() {
    if (this.done || this.loading) return;
    if (!this.model) return;
    const id = this.getOldestParent(this.model).parentMessageId;
    if (!id) return;
    this.loading = true;
    await this.axios
      .get<MessageViewModel>("/messages/" + id)
      .then((res) => {
        if (!this.getOldestParent(res.data).parentMessageId) {
          this.done = true;
        }
        if (this.model) {
          const temp = _.cloneDeep(this.model);
          const oldestParent = this.getOldestParent(temp);
          oldestParent.parentMessage = res.data;
          this.model = _.cloneDeep(temp);
        }
      })
      .finally(() => {
        this.loading = false;
      });

    if (this.scrollEl) {
      if (
        this.scrollEl.scrollHeight > 0 &&
        this.scrollEl.scrollHeight <= this.scrollEl.clientHeight &&
        !this.done
      ) {
        await this.loadMore();
      }
    }
  }

  formatTimezoneFromUtcToLocal(date: Date){
    const unformatedTimeZone = this.activeBusinessUnit?.timeZoneId;
    const timeZone = unformatedTimeZone ? unformatedTimeZone.substring(0, unformatedTimeZone.indexOf(",")) : undefined;
    return formatDateTimeAccordingToTimeZoneOrUTC(date, this.language ?? "", timeZone);
  }
}
